import { AuthApi } from './authApi';
import { ApiError, createApiError } from '../types/errors.types';

export interface ApiTestResult {
  success: boolean;
  status: number;
  message: string;
  data?: unknown;
  error?: ApiError;
}

export class ApiTestService {
  static async testProtectedEndpoint(token?: string): Promise<ApiTestResult> {
    try {
      const response = await AuthApi.testProtectedEndpoint(token);
      
      if (response.status === 401) {
        return {
          success: true,
          status: response.status,
          message: 'Expected 401 Unauthorized response received. Endpoint is properly protected.'
        };
      }

      const data = await response.json().catch(() => ({ error: 'Unknown response' }));
      
      return {
        success: false,
        status: response.status,
        message: `Unexpected response! Expected: 401 Unauthorized, Got: ${response.status}`,
        data
      };
    } catch (error) {
      const apiError = createApiError(
        error instanceof Error ? error.message : 'Unknown network error',
        undefined,
        'NETWORK_ERROR'
      );

      return {
        success: false,
        status: 0,
        message: `Network error: ${apiError.message}`,
        error: apiError
      };
    }
  }

  static formatTestResult(result: ApiTestResult): string {
    if (result.success && result.status === 401) {
      return `✅ Test successful!\n\nExpected 401 Unauthorized response received.\nStatus: ${result.status}\n${result.message}`;
    }

    if (!result.success && result.error?.code === 'NETWORK_ERROR') {
      return `❌ Network Error!\n\n${result.message}`;
    }

    if (!result.success) {
      return `❌ Unexpected Response!\n\nStatus: ${result.status}\nResponse: ${JSON.stringify(result.data, null, 2)}\n\nExpected: 401 Unauthorized`;
    }

    return `✅ ${result.message}`;
  }

  static async testAndAlert(token?: string): Promise<void> {
    const result = await this.testProtectedEndpoint(token);
    const message = this.formatTestResult(result);
    alert(message);
  }
}